const QUEUE_KEY = "gsm_offline_queue";

// Anchored (not substring) method+path allow-list. Anchoring matters: a
// naive substring match on "/players" would also match "/players/voice-parse"
// and "/players/excel-parse", which must NEVER be queued (see note below).
const QUEUEABLE_REQUESTS = [
  // --- Score entry (unchanged, existing behaviour) ---
  { method: "post", regex: /^\/scores\/save$/ },
  { method: "post", regex: /^\/scores\/clear$/ },

  // --- Players.jsx ---
  { method: "post", regex: /^\/teams\/setup$/ },
  { method: "post", regex: /^\/players$/ },                                   // add player
  { method: "put",  regex: /^\/players\/\d+$/ },                              // handicap / tee-colour edit
  { method: "put",  regex: /^\/events\/\d+\/players\/\d+\/hcp-adj-override$/ },
  { method: "put",  regex: /^\/events\/\d+$/ },                               // event field edits -- ALSO hit by
                                                                                // Course.jsx's course-link PUT;
                                                                                // Course.jsx guards its own call
                                                                                // sites with isOnline instead of
                                                                                // relying on this list to reject it.
  { method: "post", regex: /^\/events\/\d+\/players$/ },                      // toggle playing/buggy/captain/etc.
  { method: "post", regex: /^\/teams\/assign$/ },

  // --- Teams.jsx ---
  { method: "post", regex: /^\/teams\/clearall$/ },

  // --- Groups.jsx ---
  { method: "post", regex: /^\/groups\/save$/ },        // group number/bingo write (edit box, drag/drop, replace)
  { method: "post", regex: /^\/groups\/markers$/ },
  { method: "put",  regex: /^\/events\/\d+\/teetimes$/ },

  // Deliberately NOT queued (full list, with reasons):
  //   POST /players/voice-parse, POST /players/excel-parse
  //     -- live parse-and-return-for-review calls; queueing would claim
  //        success while never returning data to review. Silent data loss.
  //   POST /events                         -- new event creation; no
  //        server-assigned id available offline.
  //   DELETE anything                      -- never matched by any regex
  //        above (all method: "delete" is absent), enforced structurally.
  //   POST /groups/autogroup, /groups/reorder, /groups/add-ghost,
  //   /groups/remove-ghost, /groups/snapshot, /groups/snapshot/restore
  //     -- these compute a result server-side and the UI applies the
  //        *response* immediately. Queueing them would silently defer a
  //        computation the user is watching happen, with no result to show.
  //   PUT /courses/:id, PUT /courses/:id/holes/:hole, POST /courses,
  //   DELETE /courses/:id
  //     -- Course.jsx stays read-only offline. Never queued.
];

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error("Failed to persist offline queue:", e);
  }
  changeListeners.forEach(fn => fn(queue.length));
}

let changeListeners = [];
export function onQueueChange(fn) {
  changeListeners.push(fn);
  return () => { changeListeners = changeListeners.filter(l => l !== fn); };
}

let failureListeners = [];
export function onQueueItemFailed(fn) {
  failureListeners.push(fn);
  return () => { failureListeners = failureListeners.filter(l => l !== fn); };
}

export function isQueueableRequest(method, url) {
  if (typeof url !== "string" || typeof method !== "string") return false;
  const m = method.toLowerCase();
  return QUEUEABLE_REQUESTS.some(r => r.method === m && r.regex.test(url));
}

// Axios sets `error.request` but no `error.response` when the request never
// reached a server (offline/DNS/timeout) — as opposed to a 4xx/5xx, which
// has a response and should NOT be treated as "still offline".
export function isNetworkError(error) {
  // Treat ANY error that isn't a genuine HTTP response from the server as
  // network-shaped and safe to queue. Real-world connectivity failures (DNS
  // resolution under airplane mode, a WebView-specific XHR error shape, a
  // dropped connection mid-request) don't always produce the textbook
  // "request made, no response" axios error shape this used to require --
  // and user-entered score data must never be silently discarded just
  // because a particular failure mode didn't match exactly. Queueing
  // something that turns out to be retryable is always safer than dropping
  // it; a genuine server rejection always has error.response set, so this
  // still correctly falls through to the real-failure path below.
  return !error.response;
}

export function enqueue(url, method, body) {
  const queue = readQueue();
  queue.push({
    id: Date.now() + "-" + Math.random().toString(36).slice(2),
    url,
    method,
    body,
    ts: Date.now(),
  });
  writeQueue(queue);
}

export function queueLength() {
  return readQueue().length;
}

// Score entry re-loads its data fresh every time you navigate to it. Without
// this, a score already typed and safely queued (but not yet applied
// server-side) would appear to vanish the moment you leave and come back --
// the reload has no way to know it exists. Each queued /scores/save body is
// a full snapshot (not a diff -- see saveAllScores), so merging them in
// chronological order and letting later ones win reproduces the same result
// as if they'd all already been applied server-side.
export function getQueuedScores(eventId) {
  const overrides = {};
  readQueue()
    .filter(item => item.method === "post" && item.url === "/scores/save" && item.body?.event_id === eventId)
    .forEach(item => {
      (item.body.scores || []).forEach(s => {
        if (!overrides[s.player_id]) overrides[s.player_id] = {};
        overrides[s.player_id][s.hole] = s.gross;
      });
    });
  return overrides;
}

export function queuedMessage(res, onlineMessage) {
  return res && res.data && res.data.queued
    ? "Saved offline — will sync when reconnected"
    : onlineMessage;
}

let flushing = false;
export async function flushQueue(apiInstance) {
  if (flushing) return;
  flushing = true;
  try {
    let queue = readQueue();
    while (queue.length > 0) {
      const item = queue[0];
      try {
        await apiInstance.request({ url: item.url, method: item.method, data: item.body });
        queue = queue.slice(1);
        writeQueue(queue);
      } catch (err) {
        if (isNetworkError(err)) {
          break; // still offline — stop here, retry on the next trigger
        }
        // Server actually rejected it (4xx/5xx) — don't retry forever, surface it instead.
        queue = queue.slice(1);
        writeQueue(queue);
        failureListeners.forEach(fn => fn(item, err));
      }
    }
  } finally {
    flushing = false;
  }
}
